// Pure helpers extracted from guide-content.ts so they can be
// exercised by the Node test runner. The runtime entry list lives in
// guide-content.ts and depends on Vite's import.meta.glob, which is
// not available outside a Vite transform.

import yaml from 'js-yaml';

export type GuideCategory = 'common' | 'less_common' | 'awareness' | 'reference';

export interface GuideFrontmatter {
  title: string;
  slug: string;
  category: GuideCategory;
  last_reviewed: string;
  last_reviewed_by: string;
  visible_to_roles?: string[];
  estimated_time_minutes?: number;
  related_scenarios?: string[];
  related_architecture_sections?: string[];
}

export interface GuideEntry {
  slug: string;
  frontmatter: GuideFrontmatter;
  body: string;
}

export interface SectionedEntries {
  common: GuideEntry[];
  less_common: GuideEntry[];
  awareness: GuideEntry[];
}

export interface GuideSearchHit {
  entry: GuideEntry;
  snippet: string;
  matched: 'title' | 'body' | 'category' | 'related';
}

export function parseEntry(path: string, content: string): GuideEntry {
  if (!content.startsWith('---\n')) {
    throw new Error(`Guide entry ${path} missing frontmatter`);
  }
  const end = content.indexOf('\n---\n', 4);
  if (end === -1) {
    throw new Error(`Guide entry ${path} has malformed frontmatter delimiter`);
  }
  const frontmatterText = content.slice(4, end);
  const body = content.slice(end + 5);
  const parsed = yaml.load(frontmatterText);
  if (parsed === null || typeof parsed !== 'object') {
    throw new Error(`Guide entry ${path} frontmatter is not a YAML map`);
  }
  const frontmatter = parsed as GuideFrontmatter;
  if (!frontmatter.slug) {
    throw new Error(`Guide entry ${path} frontmatter missing slug`);
  }
  return { slug: frontmatter.slug, frontmatter, body };
}

// Sectioned view of the entries per INTERFACE-SPECIFICATION.md §14.2.
// Common and less_common sections show only entries visible to the
// authenticated user's role set; awareness collects entries the user
// does not currently propose or review but should know about. The
// awareness category in frontmatter and out-of-role visibility both
// route to the awareness section. Reference entries (_index,
// glossary, roles-explained) are omitted; the wireframe places them
// in the Guide footer.
export function sectionByRoleSet(
  entries: readonly GuideEntry[],
  roles: readonly string[]
): SectionedEntries {
  const roleSet = new Set(roles);
  const common: GuideEntry[] = [];
  const less_common: GuideEntry[] = [];
  const awareness: GuideEntry[] = [];
  for (const entry of entries) {
    if (entry.frontmatter.category === 'reference') continue;
    const visibleRoles = entry.frontmatter.visible_to_roles || [];
    const isVisibleToRole =
      visibleRoles.length === 0 || visibleRoles.some((role) => roleSet.has(role));
    if (!isVisibleToRole) {
      awareness.push(entry);
      continue;
    }
    if (entry.frontmatter.category === 'common') common.push(entry);
    else if (entry.frontmatter.category === 'less_common') less_common.push(entry);
    else if (entry.frontmatter.category === 'awareness') awareness.push(entry);
  }
  return { common, less_common, awareness };
}

// Plain-text keyword search across the Guide. Per
// INTERFACE-SPECIFICATION.md §14.6, MVP search is a case-insensitive
// substring match against scenario titles, body text, category, and
// related-scenarios slugs. The corpus is under twenty entries; a
// linear scan is fast enough that index machinery would only add
// complexity.
export function searchGuide(entries: readonly GuideEntry[], query: string): GuideSearchHit[] {
  const normalised = query.trim().toLowerCase();
  if (normalised.length === 0) return [];
  const hits: GuideSearchHit[] = [];
  for (const entry of entries) {
    if (entry.frontmatter.title.toLowerCase().includes(normalised)) {
      hits.push({ entry, snippet: entry.frontmatter.title, matched: 'title' });
      continue;
    }
    const lowerBody = entry.body.toLowerCase();
    const bodyIdx = lowerBody.indexOf(normalised);
    if (bodyIdx !== -1) {
      hits.push({
        entry,
        snippet: extractSnippet(entry.body, bodyIdx, normalised.length),
        matched: 'body'
      });
      continue;
    }
    if (entry.frontmatter.category && entry.frontmatter.category.toLowerCase().includes(normalised)) {
      hits.push({ entry, snippet: `Category: ${entry.frontmatter.category}`, matched: 'category' });
      continue;
    }
    const relatedHit = (entry.frontmatter.related_scenarios || []).find(
      (slug) => slug.toLowerCase().includes(normalised)
    );
    if (relatedHit) {
      hits.push({ entry, snippet: `Related: ${relatedHit}`, matched: 'related' });
      continue;
    }
  }
  return hits;
}

export function extractSnippet(body: string, matchStart: number, matchLength: number): string {
  // Find the line containing the match so the snippet reads as a
  // self-contained sentence, then collapse markdown emphasis to
  // plain text. Snippet caps at 160 characters with ellipsis when
  // the line extends past either end.
  const lineStart = body.lastIndexOf('\n', matchStart) + 1;
  const lineEndRaw = body.indexOf('\n', matchStart + matchLength);
  const lineEnd = lineEndRaw === -1 ? body.length : lineEndRaw;
  const rawLine = body.slice(lineStart, lineEnd).trim();
  const stripped = rawLine.replace(/[`*_]/g, '');
  if (stripped.length <= 160) return stripped;
  const relativeMatch = matchStart - lineStart;
  const halfWindow = 80;
  const windowStart = Math.max(0, relativeMatch - halfWindow);
  const windowEnd = Math.min(stripped.length, relativeMatch + matchLength + halfWindow);
  const prefix = windowStart === 0 ? '' : '… ';
  const suffix = windowEnd === stripped.length ? '' : ' …';
  return prefix + stripped.slice(windowStart, windowEnd) + suffix;
}
